<template>
    <div v-if="!isCarrierMarketplaceEnabled">
        <OrderGridOverview />
    </div>
    <div class="main-container" v-else>
        <tabs
            :tab-name="tabNames"
            @switch-panel="switchTab"
            :selected-panel="activeTab"
            color-button="success"
            class="custom-tab-list"
        >
            <template slot="tab-pane-1">
                <OrderGridOverview />
            </template>

            <template slot="tab-pane-2">
                <OrdersDashboard :show-carrier-orders="true" />
            </template>
        </tabs>
    </div>
</template>

<script>
import { FEATURE_NAMES } from '@/utils/constants';
import { FeatureManager } from '@/directives';
import Tabs from '@/components/Tabs';
import OrdersDashboard from '@/pages/Orders/components/OrdersDashboard';
import OrderGridOverview from '@/pages/Orders/OrderGridOverview';

const TABS = [
    {
        label: 'Standard Orders',
        value: 'standardOrders'
    },
    {
        label: 'Carrier Orders',
        value: 'carrierOrders'
    }
];

export default {
    name: 'OrdersOverview',
    components: { OrderGridOverview, OrdersDashboard, Tabs },
    mixins: [FeatureManager],
    data() {
        return {
            featureNames: FEATURE_NAMES,
            activeTab: TABS[0].label
        };
    },
    computed: {
        isCarrierMarketplaceEnabled() {
            return this.$_hasFeature(FEATURE_NAMES.CarrierMarketplace);
        },
        tabNames() {
            return TABS.map((x) => x.label);
        }
    },
    methods: {
        calculateColumnWidths() {
            this.$nextTick(() => {
                // Calculate widths for first 3 columns
                const newWidths = [46, 100, 100];
                let cellHeight = 0;
                this.columnWidths = [46, 100, 100];

                // Calculate the maximum height for each column based on content
                for (let row = 0; row < this.orderList.length; row++) {
                    for (let col = 0; col < 11; col++) {
                        const cell = this.$refs[`bodyCell${row}-${col + 1}`];
                        if (cell) {
                            const cellEl = cell[0].$el;

                            if (cellEl) {
                                cellHeight = Math.max(cellEl.offsetHeight, cellHeight);
                            }
                        }
                    }
                }

                // Calculate the maximum width for each column based on content, we only check the first 3 columns
                for (let row = 0; row < this.orderList.length; row++) {
                    for (let col = 0; col < 3; col++) {
                        const cell = this.$refs[`bodyCell${row}-${col + 1}`];
                        if (cell) {
                            const cellEl = cell[0].$el;
                            if (cellEl) {
                                let finalWidth = cellEl.scrollWidth;
                                if (col > 0 && finalWidth < 100) {
                                    finalWidth = 100; // Ensure minimum width of 100px
                                }

                                if (col === 1 && finalWidth > 105) {
                                    finalWidth = 140; // for the order number or source reference, we want to ensure it fits well
                                }

                                if (col === 2 && finalWidth > 120) {
                                    finalWidth = 160; // the longest length of the status will fit this width
                                }
                                newWidths[col] = Math.max(newWidths[col], finalWidth);
                            }
                        }
                    }
                }

                this.columnWidths = newWidths;
                this.scrollHeight = cellHeight;

                // Update CSS custom properties
                document.documentElement.style.setProperty('--col1-width', `${this.columnWidths[0]}px`);
                document.documentElement.style.setProperty('--col2-width', `${this.columnWidths[1]}px`);
                document.documentElement.style.setProperty('--col3-width', `${this.columnWidths[2]}px`);
                document.documentElement.style.setProperty('--table-cell-height', `${this.scrollHeight}px`);
            });
        },
        async fetchOrdersList(pageNumber = 1, itemsPerPage = 50) {
            try {
                this.isListLoading = true;
                const endpoint = `/api/orders/list`;
                const response = await handleRequests(endpoint, {
                    params: {
                        pageNumber,
                        itemsPerPage,
                        searchText: this.currentSearchTextValue,
                        dateFilterType: this.dateFilterType,
                        fromDate: this.fromDate,
                        toDate: this.toDate,
                        serviceAreaId: this.serviceAreaId,
                        customerId: this.customerId,
                        orderStatus: this.orderStatus,
                        rateGroupId: this.rateGroupId
                    }
                });

                this.orderList = response.data.orders;
                this.pagination.total = response.data.totalOrders;
                this.pagination.perPage = itemsPerPage;
                this.isListLoading = false;
                this.calculateColumnWidths();
            } catch (error) {
                const message = 'Error in fetching order list';
                showErrorMessage(this, message, error);
                this.isListLoading = false;
            }
        },
        handleSelectedColumn(item) {
            this.selectedColumn = item;
            this.calculateColumnWidths();
        },
        async handleChangePage(currentPage = 1, perPage = 50) {
            this.pagination.currentPage = currentPage;
            this.pagination.perPage = perPage;
            this.fetchOrdersList(currentPage, perPage);
            this.$router.replace({ path: this.$route.path, query: { currentPage } });
        },
        // eslint-disable-next-line func-names
        handleFilterOrSearch: _.debounce(function(val) {
            this.currentSearchTextValue = val.searchText;
            this.fetchOrdersList();
        }, 1000),
        handleFilterChange(filter) {
            this.customerId = filter.customerId;
            this.serviceAreaId = filter.serviceAreaId;
            this.dateFilterType = filter.filterType;
            this.fromDate = filter.fromDate;
            this.toDate = filter.toDate;
            this.selectedOrders = [];
            this.orderStatus = filter.orderStatus;
            this.rateGroupId = filter.rateGroupId;
            this.fetchOrdersList();
        },
        onSelectAll(value) {
            if (value) {
                this.orderList.forEach((order) => {
                    const isSelected = this.selectedOrders.some(
                        (selectedOrder) => selectedOrder.orderId === order.orderId
                    );

                    if (isSelected) {
                        return;
                    }
                    this.selectedOrders.push(order);
                });
            } else {
                this.selectedOrders = [];
            }
        },
        singleCheckbox(order) {
            const isRemoved = !this.selectedOrders.some((selectedOrder) => selectedOrder.orderId === order.orderId);
            if (isRemoved) {
                this.isSelectAll = false;
            }
        },
        createHandleScroll() {
            document.querySelector('.main-panel').addEventListener('scroll', this.handleScroll, true);
        },
        handleScroll(e) {
            this.topScrollPosition = e.target.scrollTop;
        },
        sendSchedulingLink() {
            this.$modal
                .show(SendBulkSms, {
                    title: 'Send Bulk SMS',
                    selectedOrders: this.selectedOrders
                })
                .then((response) => {
                    if (response === 'ok') 
                        this.$modal.hide();
                });
        },
        updateOrder(order) {
            this.$modal
                .show(UpdateOrderModal, {
                    orderId: order.orderId
                })
                .then((response) => {
                    if (response.code.toLowerCase() === 'ok') {
                        this.$modal.hide();
                        this.fetchOrdersList();
                    }
                });
        },
        async updateOrderStatus(order, action) {
            this.$_handleLoaderState(true, 'UPDATING...');
            const api = `/api/orders/${order.orderId}/${action}`;
            const payload = {
                method: 'post'
            };

            try {
                await handleRequests(api, payload);
                this.$notify({
                    message: 'Successfully updated the status.',
                    type: 'success'
                });
                this.fetchOrdersList();
            } catch (e) {
                const message = 'Could not update order status.';
                showErrorMessage(this, message, e);
            }
            this.$_handleLoaderState(false);
        },
        putOrderOnHold(order) {
            let title = 'Order on Hold';
            let bodyMessage =
                'Are you sure you want to put this order on hold? This will also put the related stops to an on hold status.';
            if (order.status === 'On Hold') {
                title = 'Order Unhold';
                bodyMessage = 'Are you sure you want to unhold this order? This will also unhold the related stops.';
            }

            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title,
                    body: bodyMessage,
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        let apiAction = 'on-hold';
                        if (order.status === 'On Hold') {
                            apiAction = 'unhold';
                        }
                        await this.updateOrderStatus(order, apiAction);
                    }
                });
        },
        /**
         *
         * @param {string} tab
         */
        switchTab(tab) {
            this.activeTab = tab;
        }
    }
};
</script>

<style lang="scss" scoped>
.main-container {
    ::v-deep .custom-tab-list {
        padding: 0 20px;
    }
    ::v-deep .tab-content {
        margin-top: 30px;
    }
}
</style>
